import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuthStore } from '../../stores/authStore';
import { StethoscopeIcon, PersonIcon, FamilyIcon, LockIcon, GlobeIcon, EyeIcon, DeviceIcon, CheckIcon } from '../icons/SvgIcons';

export const LandingPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, checkAuth, logout } = useAuthStore();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const handleGetStarted = () => {
    navigate(isAuthenticated ? '/dashboard' : '/login');
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#FAFBFC', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      {/* Header */}
      <header style={{
        borderBottom: '1px solid #E2E8F0',
        backgroundColor: 'rgba(255,255,255,0.85)',
        backdropFilter: 'blur(12px)',
        position: 'sticky',
        top: 0,
        zIndex: 50,
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '14px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{
            fontSize: '1.3rem',
            fontWeight: 800,
            letterSpacing: '-0.03em',
            background: 'linear-gradient(135deg, #2563EB, #7C3AED)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            Vincula
          </span>
          <nav style={{ display: 'flex', alignItems: 'center', gap: 28 }}>
            <a href="#features" style={{ color: '#64748B', textDecoration: 'none', fontSize: '0.875rem', fontWeight: 500 }}>Características</a>
            <a href="#how-it-works" style={{ color: '#64748B', textDecoration: 'none', fontSize: '0.875rem', fontWeight: 500 }}>Cómo funciona</a>
            {isAuthenticated ? (
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <span style={{ fontSize: '0.8125rem', color: '#64748B' }}>Hola, {user?.first_name || 'Usuario'}</span>
                <button onClick={handleGetStarted} style={btnPrimary}>Dashboard</button>
                <button onClick={logout} style={btnGhost}>Salir</button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => navigate('/login')} style={btnGhost}>Iniciar sesión</button>
                <button onClick={() => navigate('/register')} style={btnPrimary}>Crear cuenta</button>
              </div>
            )}
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section style={{ maxWidth: 1200, margin: '0 auto', padding: '80px 24px 60px', textAlign: 'center' }}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 16px',
            borderRadius: 100,
            border: '1px solid #E2E8F0',
            backgroundColor: 'white',
            fontSize: '0.8125rem',
            color: '#64748B',
            marginBottom: 24,
            fontWeight: 500,
          }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#10B981' }} />
            Plataforma de acompañamiento virtual
          </div>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          style={{
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            fontWeight: 800,
            letterSpacing: '-0.03em',
            color: '#0F172A',
            lineHeight: 1.1,
            maxWidth: 720,
            margin: '0 auto 20px',
          }}
        >
          Acompaña a tu ser querido desde{' '}
          <span style={{ background: 'linear-gradient(135deg, #2563EB, #7C3AED)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            cualquier lugar
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          style={{ fontSize: '1.125rem', color: '#64748B', maxWidth: 560, margin: '0 auto 36px', lineHeight: 1.6 }}
        >
          Vincula conecta familiares y acompañantes con videollamadas en tiempo real.
          Todas las sesiones se graban automáticamente.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}
        >
          <button onClick={() => navigate('/register')} style={{ ...btnPrimary, padding: '14px 32px', fontSize: '1rem' }}>
            Empezar gratis
          </button>
          <button onClick={handleGetStarted} style={{ ...btnOutlined, padding: '14px 32px', fontSize: '1rem' }}>
            Iniciar sesión
          </button>
        </motion.div>

        {/* Hero visual */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          style={{ marginTop: 60 }}
        >
          <div style={{
            maxWidth: 800,
            margin: '0 auto',
            padding: 32,
            borderRadius: 20,
            border: '1px solid #E2E8F0',
            backgroundColor: 'white',
            boxShadow: '0 20px 60px rgba(0,0,0,0.06)',
          }}>
            <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
              {[
                { label: 'Profesional', color: '#2563EB', bg: '#EFF6FF', icon: <StethoscopeIcon width={40} height={40} color="#2563EB" /> },
                { label: 'Paciente', color: '#059669', bg: '#F0FDF4', icon: <PersonIcon width={40} height={40} color="#059669" /> },
                { label: 'Familiar', color: '#7C3AED', bg: '#F5F3FF', icon: <FamilyIcon width={40} height={40} color="#7C3AED" /> },
              ].map((p) => (
                <div key={p.label} style={{
                  flex: 1,
                  minWidth: 160,
                  padding: 24,
                  borderRadius: 14,
                  backgroundColor: p.bg,
                  textAlign: 'center',
                  border: `1px solid ${p.bg}`,
                }}>
                  <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'center' }}>{p.icon}</div>
                  <div style={{ fontWeight: 600, color: p.color, fontSize: '0.9375rem' }}>{p.label}</div>
                  <div style={{ fontSize: '0.75rem', color: '#64748B', marginTop: 4 }}>Conectado</div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </section>

      {/* Features */}
      <section id="features" style={{ maxWidth: 1200, margin: '0 auto', padding: '60px 24px' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#0F172A', letterSpacing: '-0.025em', marginBottom: 8 }}>
            Diseñado para cada rol
          </h2>
          <p style={{ color: '#64748B', fontSize: '1rem', maxWidth: 480, margin: '0 auto' }}>
            Cada persona tiene una experiencia personalizada según sus necesidades
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
          {[
            {
              title: 'Profesionales',
              icon: <StethoscopeIcon width={24} height={24} color="#2563EB" />,
              color: '#2563EB',
              bg: '#EFF6FF',
              items: ['Cola inteligente por prioridad', 'Grabación automática', 'Resúmenes con IA', 'Comunicación con familiares'],
            },
            {
              title: 'Pacientes',
              icon: <PersonIcon width={24} height={24} color="#059669" />,
              color: '#059669',
              bg: '#F0FDF4',
              items: ['Solicita acompañamiento fácilmente', 'Acompañamiento 24/7', 'Historial completo', 'Acceso desde cualquier lugar'],
            },
            {
              title: 'Familiares',
              icon: <FamilyIcon width={24} height={24} color="#7C3AED" />,
              color: '#7C3AED',
              bg: '#F5F3FF',
              items: ['Acompaña en tiempo real', 'Modo observador silencioso', 'Acceso a grabaciones', 'Resúmenes automáticos'],
            },
          ].map((card) => (
            <motion.div
              key={card.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              viewport={{ once: true }}
              style={{
                padding: 28,
                borderRadius: 16,
                border: '1px solid #E2E8F0',
                backgroundColor: 'white',
              }}
            >
              <div style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                backgroundColor: card.bg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 16,
              }}>
                {card.icon}
              </div>
              <h3 style={{ fontSize: '1.125rem', fontWeight: 700, color: '#0F172A', marginBottom: 12 }}>{card.title}</h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {card.items.map((item) => (
                  <li key={item} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontSize: '0.875rem', color: '#475569' }}>
                    <CheckIcon color={card.color} />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" style={{ backgroundColor: 'white', borderTop: '1px solid #E2E8F0', borderBottom: '1px solid #E2E8F0' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '60px 24px' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#0F172A', letterSpacing: '-0.025em', marginBottom: 8 }}>
              Cómo funciona
            </h2>
            <p style={{ color: '#64748B', fontSize: '1rem' }}>Tres pasos para conectar</p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 32 }}>
            {[
              { n: '01', title: 'Solicita', desc: 'Los pacientes solicitan acompañamiento o los profesionales programan sesiones.' },
              { n: '02', title: 'Conecta', desc: 'Únete a la videollamada desde cualquier dispositivo. Los familiares pueden observar o participar.' },
              { n: '03', title: 'Revisa', desc: 'Todas las sesiones se graban. Accede al historial y resúmenes inteligentes en cualquier momento.' },
            ].map((step) => (
              <motion.div
                key={step.n}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                viewport={{ once: true }}
              >
                <div style={{
                  fontSize: '2.5rem',
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #2563EB, #7C3AED)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  marginBottom: 8,
                  letterSpacing: '-0.03em',
                }}>
                  {step.n}
                </div>
                <h3 style={{ fontSize: '1.125rem', fontWeight: 700, color: '#0F172A', marginBottom: 8 }}>{step.title}</h3>
                <p style={{ fontSize: '0.875rem', color: '#64748B', lineHeight: 1.6, margin: 0 }}>{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section style={{ maxWidth: 1200, margin: '0 auto', padding: '60px 24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
          {[
            { icon: <LockIcon width={28} height={28} color="#2563EB" />, title: 'Seguro', desc: 'Datos protegidos bajo normativas médicas' },
            { icon: <GlobeIcon width={28} height={28} color="#059669" />, title: 'Universal', desc: 'Funciona en cualquier dispositivo' },
            { icon: <EyeIcon width={28} height={28} color="#7C3AED" />, title: 'Modo observador', desc: 'Acompaña sin interrumpir' },
            { icon: <DeviceIcon width={28} height={28} color="#0EA5E9" />, title: 'Multiplataforma', desc: 'PC, tablet y móvil' },
          ].map((b) => (
            <div key={b.title} style={{
              padding: 24,
              borderRadius: 14,
              border: '1px solid #E2E8F0',
              backgroundColor: 'white',
            }}>
              <div style={{ marginBottom: 10 }}>{b.icon}</div>
              <div style={{ fontWeight: 700, color: '#0F172A', fontSize: '0.9375rem', marginBottom: 4 }}>{b.title}</div>
              <div style={{ color: '#64748B', fontSize: '0.8125rem', lineHeight: 1.5 }}>{b.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section style={{
        maxWidth: 1200,
        margin: '0 auto 60px',
        padding: '48px 24px',
      }}>
        <div style={{
          textAlign: 'center',
          padding: '48px 32px',
          borderRadius: 20,
          background: 'linear-gradient(135deg, #0F172A, #1E293B)',
          color: 'white',
        }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 800, marginBottom: 12, letterSpacing: '-0.025em' }}>
            ¿Listo para conectar?
          </h2>
          <p style={{ color: '#94A3B8', marginBottom: 28, fontSize: '1rem', maxWidth: 400, margin: '0 auto 28px' }}>
            Únete a las familias que ya confían en Vincula
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/register')} style={{ ...btnPrimary, padding: '14px 32px', fontSize: '1rem' }}>
              Crear cuenta gratis
            </button>
            <button onClick={handleGetStarted} style={{
              ...btnOutlined,
              padding: '14px 32px',
              fontSize: '1rem',
              borderColor: '#475569',
              color: 'white',
            }}>
              Iniciar sesión
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ borderTop: '1px solid #E2E8F0', backgroundColor: 'white' }}>
        <div style={{
          maxWidth: 1200,
          margin: '0 auto',
          padding: '32px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 16,
        }}>
          <span style={{
            fontWeight: 800,
            fontSize: '1.1rem',
            background: 'linear-gradient(135deg, #2563EB, #7C3AED)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            Vincula
          </span>
          <span style={{ color: '#94A3B8', fontSize: '0.8125rem' }}>
            &copy; {new Date().getFullYear()} Vincula. Todos los derechos reservados.
          </span>
        </div>
      </footer>
    </div>
  );
};

const btnPrimary = {
  padding: '10px 20px',
  borderRadius: 10,
  border: 'none',
  backgroundColor: '#2563EB',
  color: 'white',
  fontWeight: 600,
  fontSize: '0.875rem',
  cursor: 'pointer',
  fontFamily: 'inherit',
  transition: 'background-color 0.15s',
};

const btnOutlined = {
  padding: '10px 20px',
  borderRadius: 10,
  border: '1px solid #E2E8F0',
  backgroundColor: 'transparent',
  color: '#334155',
  fontWeight: 600,
  fontSize: '0.875rem',
  cursor: 'pointer',
  fontFamily: 'inherit',
  transition: 'border-color 0.15s',
};

const btnGhost = {
  padding: '10px 16px',
  borderRadius: 10,
  border: 'none',
  backgroundColor: 'transparent',
  color: '#64748B',
  fontWeight: 500,
  fontSize: '0.875rem',
  cursor: 'pointer',
  fontFamily: 'inherit',
};
